import { Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, FormControl, FormControlLabel, IconButton, InputLabel, ListItemText, MenuItem, Pagination, Select, SelectChangeEvent, Snackbar, Stack, Switch, TextField, Tooltip, Typography } from "@mui/material";
import React from "react";
import { cancelOffer, generateOffer, getFunnelList, getUserOffersPaginated, testFunnelEligibility } from "../Api";
import UserOfferDto from "../models/dtos/UserOfferDto";
import FunnelConfigDto from "../models/dtos/FunnelConfigDto";
import { DataGrid } from "@mui/x-data-grid/DataGrid/DataGrid";
import { GridColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import { GridCellParams, GridValueGetterParams } from "@mui/x-data-grid/models/params/gridCellParams";
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import TargetDevicesCell from './TargetDevicesCell';

const PRICE_TIERS: { value: string; label: string; price: string }[] = [
    { value: 'BASE', label: 'Base', price: '$0.99' },
    { value: 'STARTER', label: 'Starter', price: '$1.99' },
    { value: 'MEDIUM', label: 'Medium', price: '$2.99' },
    { value: 'ADVANCED', label: 'Advanced', price: '$4.99' },
    { value: 'SPECIAL', label: 'Special', price: '$9.99' },
    { value: 'HUGE', label: 'Huge', price: '$14.99' },
    { value: 'MEGA', label: 'Mega', price: '$19.99' },
    { value: 'ULTRA', label: 'Ultra', price: '$29.99' },
    { value: 'MAX', label: 'Max', price: '$49.99' },
    { value: 'ABSOLUTE', label: 'Absolute', price: '$99.99' }
];

const OFFER_LANGUAGES = ['en', 'ru', 'ro', 'it', 'fr', 'de', 'es', 'pt', 'pl', 'tr', 'hi'];

interface UserOffersTableProps {
    userId: number;
    userLang?: string;
}

export default function UserOffersTable(props: UserOffersTableProps) {

    const defaultLang = props.userLang && OFFER_LANGUAGES.includes(props.userLang) ? props.userLang : 'en';

    const [state, setState] = React.useState({
        items: [] as UserOfferDto[],
        funnels: [] as FunnelConfigDto[],
        loaded: false,
        page: 1,
        totalPages: 1,
        pageSize: 25,
        selectedPriceTier: 'BASE',
        selectedFunnelId: '',
        bonusSize: 0,
        generating: false,
        testingEligibility: false,
        toastMsg: null as string | null,
        lang: defaultLang,
        useCustomBetcoins: false,
        customBetcoins: 0
    });

    const [previewOffer, setPreviewOffer] = React.useState<UserOfferDto | null>(null);
    const [htmlPreviewContent, setHtmlPreviewContent] = React.useState<string | null>(null);
    const [htmlPreviewLoading, setHtmlPreviewLoading] = React.useState(false);

    React.useEffect(() => {
        if (previewOffer?.htmlTemplateData) {
            setHtmlPreviewContent(null);
            setHtmlPreviewLoading(true);
            const url = new URL(previewOffer.htmlTemplateData.url);
            url.searchParams.set('preview', 'true');
            fetch(url.toString())
                .then(r => r.text())
                .then(html => { setHtmlPreviewContent(html); setHtmlPreviewLoading(false); })
                .catch(() => { setHtmlPreviewContent('<p>Failed to load preview.</p>'); setHtmlPreviewLoading(false); });
        } else {
            setHtmlPreviewContent(null);
        }
    }, [previewOffer]);

    const onPreviewClicked = (offer: UserOfferDto) => {
        setPreviewOffer(offer);
    };

    const onCancelOfferClicked = async (offerId: string) => {
        await cancelOffer(offerId);
        await fetchData(state.page);
    }

    const onGenerateOfferClicked = async () => {
        if (!state.selectedFunnelId) {
            setState(prevState => ({ ...prevState, toastMsg: 'Please select a funnel.' }));
            return;
        }
        if (!state.useCustomBetcoins && (state.bonusSize < 0 || state.bonusSize > 10 || isNaN(state.bonusSize))) {
            setState(prevState => ({ ...prevState, toastMsg: 'Bonus size must be between 0 and 10.' }));
            return;
        }
        if (state.useCustomBetcoins && (state.customBetcoins < 0 || state.customBetcoins > 1000000000)) {
            setState(prevState => ({ ...prevState, toastMsg: 'Custom betcoins must be between 0 and 1,000,000,000.' }));
            return;
        }
        setState(prevState => ({ ...prevState, generating: true }));
        const result = await generateOffer(
            props.userId,
            state.selectedPriceTier,
            state.selectedFunnelId,
            state.bonusSize,
            state.lang,
            state.useCustomBetcoins ? state.customBetcoins : null
        );
        if (result != null) {
            await fetchData(state.page);
        } else {
            setState(prevState => ({ ...prevState, generating: false, toastMsg: 'Failed to generate offer. Please make sure that device meets funnel restrictions' }));
        }
    }

    const onTestFunnelClicked = async () => {
        if (!state.selectedFunnelId) {
            setState(prevState => ({ ...prevState, toastMsg: 'Please select a funnel.' }));
            return;
        }

        setState(prevState => ({ ...prevState, testingEligibility: true }));
        const result = await testFunnelEligibility(props.userId, state.selectedFunnelId);
        setState(prevState => ({
            ...prevState,
            testingEligibility: false,
            toastMsg: result.ok ? (result.data?.message ?? 'Eligibility checked.') : (result.error ?? 'Failed to test funnel eligibility.')
        }));
    }

    const columns: GridColDef[] = [
        {
            field: 'preview',
            headerName: '',
            width: 50,
            sortable: false,
            filterable: false,
            renderCell: (params: GridCellParams) => (
                <IconButton size="small" onClick={() => onPreviewClicked(params.row as UserOfferDto)}>
                    <VisibilityIcon fontSize="small"/>
                </IconButton>
            )
        },
        { field: 'userBetupId', headerName: 'UserId', width: 100 },
        {
            field: 'type',
            headerName: 'Type',
            width: 90,
            sortable: false,
            renderCell: (params: GridCellParams) => (
                <Chip
                    label={params.row.htmlTemplateId ? 'HTML' : 'Legacy'}
                    size="small"
                    color={params.row.htmlTemplateId ? 'primary' : 'default'}
                    variant="outlined"
                />
            )
        },
        {
            field: 'priceTier',
            headerName: 'Pricing tier',
            width: 150,
            sortable: false,
            renderCell: (params: GridCellParams) => {
                const offer = params.row as UserOfferDto;
                if (!offer.priceTier && offer.priceInUsd == null) return '—';
                const label = PRICE_TIERS.find(t => t.value === offer.priceTier)?.label ?? offer.priceTier;
                return (
                    <Tooltip title={offer.newPriceSku}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {label && <Typography variant="body2">{label}</Typography>}
                            {offer.priceInUsd != null &&
                                <Typography variant="body2" color="text.secondary">~${offer.priceInUsd.toFixed(2)}</Typography>}
                        </Stack>
                    </Tooltip>
                );
            }
        },
        {
            field: 'source',
            headerName: 'Funnel',
            width: 130,
            valueGetter: (params: GridValueGetterParams) => state.funnels.find(x => x.id == params.row.source)?.name ?? params.row.source
        },
        {
            field: 'targetDevices',
            headerName: 'Target devices',
            width: 170,
            sortable: false,
            renderCell: (params: GridCellParams) => (
                <TargetDevicesCell
                    osType={params.row.osType}
                    minBuildNumber={params.row.minBuildNumber}
                    maxBuildNumber={params.row.maxBuildNumber}
                />
            )
        },
        { field: 'betcoins', headerName: 'Betcoins', width: 130 },
        { field: 'newPriceSku', headerName: 'Product', width: 150 },
        {
            field: 'active',
            headerName: 'Active',
            width: 100,
            renderCell: (params: GridCellParams) => params.row.purchased == false && new Date(params.row.expiresAt) > new Date() ?
                <Typography color={"green"} variant='body1'>YES</Typography> :
                <Typography color={"red"} variant='body1'>NO</Typography>
        },
        {
            field: 'purchased',
            headerName: 'Purchased',
            width: 100,
            renderCell: (params: GridCellParams) => params.row.purchased ?
                <Typography color={"green"} variant='body1'>YES</Typography> :
                <Typography color={"red"} variant='body1'>NO</Typography>
        },
        {
            field: 'createdAt',
            headerName: 'CreatedAt',
            width: 200,
            valueGetter: (params: GridValueGetterParams) => new Date(params.row.createdAt).toLocaleString()
        },
        {
            field: 'expiresAt',
            headerName: 'ExpiresAt',
            width: 200,
            valueGetter: (params: GridValueGetterParams) => new Date(params.row.expiresAt).toLocaleString()
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            renderCell: (params: GridCellParams) => params.row.purchased == false && new Date(params.row.expiresAt) > new Date() ?
                <Button variant="contained" fullWidth={true} style={{ height: '32px' }}
                    onClick={() => onCancelOfferClicked(params.row.id)}>Cancel</Button> :
                <React.Fragment></React.Fragment>
        },
    ];

    const fetchData = async (page = 1) => {
        setState(prevState => ({ ...prevState, loaded: false }));
        const offset = (page - 1);
        const limit = state.pageSize;

        const [funnels, response] = await Promise.all([getFunnelList(), getUserOffersPaginated(props.userId, offset, limit)]);
        if (response) {
            const totalPages = Math.max(1, Math.ceil(response.totalItems / state.pageSize));
            setState(prevState => ({
                ...prevState,
                items: response.items ?? [],
                funnels: funnels,
                loaded: true,
                generating: false,
                totalPages: totalPages,
                page: page,
                selectedFunnelId: prevState.selectedFunnelId || (funnels[0]?.id ?? ''),
                bonusSize: !prevState.selectedFunnelId && funnels[0] ? funnels[0].bonusSize : prevState.bonusSize
            }));
        } else {
            setState(prevState => ({ ...prevState, funnels: funnels, loaded: true, generating: false, selectedFunnelId: prevState.selectedFunnelId || (funnels[0]?.id ?? ''), bonusSize: !prevState.selectedFunnelId && funnels[0] ? funnels[0].bonusSize : prevState.bonusSize }));
        }
    }

    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        fetchData(page);
    };

    const handlePageSelectChange = (event: SelectChangeEvent<number>) => {
        const selectedPage = Number(event.target.value);
        fetchData(selectedPage);
    };

    React.useEffect(() => {
        fetchData(1);
    }, []);

    return (
        <>
            {!state.loaded ? (
                <CircularProgress size='100px' style={{ marginBottom: '32px' }} />
            ) : (
                <>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                        {/* Row 1: Language · Price Tier · Funnel */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                            <FormControl size="small" sx={{ minWidth: 110, flex: '0 1 110px' }}>
                                <InputLabel>Language</InputLabel>
                                <Select
                                    value={state.lang}
                                    label="Language"
                                    onChange={e => setState(prev => ({ ...prev, lang: e.target.value }))}
                                >
                                    {OFFER_LANGUAGES.map(l => (
                                        <MenuItem key={l} value={l}>{l.toUpperCase()}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 200, flex: '1 1 180px' }}>
                                <InputLabel>Price Tier</InputLabel>
                                <Select
                                    value={state.selectedPriceTier}
                                    label="Price Tier"
                                    onChange={e => setState(prevState => ({ ...prevState, selectedPriceTier: e.target.value }))}
                                >
                                    {PRICE_TIERS.map(tier => (
                                        <MenuItem key={tier.value} value={tier.value}>
                                            {tier.label}&nbsp;&mdash;&nbsp;{tier.price}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Box sx={{ minWidth: 280, flex: '2 1 260px', display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FormControl size="small" sx={{ flex: 1 }}>
                                    <InputLabel>Funnel</InputLabel>
                                    <Select
                                        value={state.selectedFunnelId}
                                        label="Funnel"
                                        onChange={e => {
                                            const funnel = state.funnels.find(f => f.id === e.target.value);
                                            setState(prevState => ({ ...prevState, selectedFunnelId: e.target.value, bonusSize: funnel?.bonusSize ?? prevState.bonusSize }));
                                        }}
                                    >
                                        {state.funnels.map(f => (
                                            <MenuItem key={f.id} value={f.id}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    {f.osTypes?.map(os =>
                                                        <img key={os} src={os.toLowerCase().includes('android') ? '/android_logo.png' : '/ios_logo.png'} width={20} height={20} alt={os} />
                                                    )}
                                                    <ListItemText
                                                        primary={f.name}
                                                        secondary={
                                                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <Box component="span" sx={{ color: f.enabled ? 'green' : 'error.main', fontSize: '0.75rem' }}>
                                                                    {f.enabled ? 'Active' : 'Disabled'}
                                                                </Box>
                                                                <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                                                    &nbsp;·&nbsp;<AccessTimeIcon sx={{ fontSize: '0.85rem', verticalAlign: 'middle' }} />&nbsp;{f.lifetime}
                                                                </Box>
                                                            </Box>
                                                        }
                                                    />
                                                </Stack>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Tooltip title="Test funnel eligibility">
                                    <span>
                                        <IconButton
                                            color="primary"
                                            size="small"
                                            onClick={onTestFunnelClicked}
                                            disabled={state.testingEligibility || !state.selectedFunnelId}
                                            sx={{ border: theme => `1px solid ${theme.palette.divider}` }}
                                        >
                                            {state.testingEligibility ? <CircularProgress size={18} /> : <ScienceOutlinedIcon fontSize="small" />}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        </Box>
                        {/* Row 2: Bonus Size · toggle · Custom Betcoins · Generate */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                            <TextField
                                size="small"
                                label="Bonus Size"
                                type="number"
                                value={state.bonusSize}
                                disabled={state.useCustomBetcoins}
                                onChange={e => {
                                    const val = parseFloat(e.target.value);
                                    setState(prevState => ({ ...prevState, bonusSize: isNaN(val) ? 0 : Math.min(10, Math.max(0, val)) }));
                                }}
                                inputProps={{ min: 0, max: 10, step: 0.01 }}
                                sx={{ width: 130, flex: '0 0 auto' }}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={state.useCustomBetcoins}
                                        onChange={e => setState(prev => ({ ...prev, useCustomBetcoins: e.target.checked }))}
                                        size="small"
                                    />
                                }
                                label="Use custom betcoins"
                                sx={{ flex: '0 0 auto', whiteSpace: 'nowrap', m: 0 }}
                            />
                            <TextField
                                size="small"
                                label="Custom Betcoins"
                                type="number"
                                value={state.customBetcoins}
                                disabled={!state.useCustomBetcoins}
                                onChange={e => {
                                    const val = parseInt(e.target.value);
                                    setState(prevState => ({ ...prevState, customBetcoins: isNaN(val) ? 0 : Math.min(1000000000, Math.max(0, val)) }));
                                }}
                                inputProps={{ min: 0, max: 1000000000, step: 1 }}
                                sx={{ width: 180, flex: '0 0 auto' }}
                            />
                            <Button
                                variant="contained"
                                disabled={state.generating}
                                onClick={onGenerateOfferClicked}
                                sx={{ flex: '0 0 auto', whiteSpace: 'nowrap' }}
                            >
                                {state.generating ? 'Generating...' : 'Generate Offer'}
                            </Button>
                        </Box>
                    </Box>
                    <DataGrid sx={{ width: '100%' }}
                        rows={state.items}
                        columns={columns}
                        initialState={{
                            pagination: {
                                paginationModel: { page: 0, pageSize: 25 },
                            },
                        }}
                        pageSizeOptions={[5, 10, 25]}
                        hideFooterPagination
                        hideFooter
                    />
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{ mt: 3, mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        <Pagination
                            count={state.totalPages}
                            page={state.page}
                            onChange={handlePageChange}
                            color="primary"
                            showFirstButton
                            showLastButton
                        />
                        <Select
                            value={state.page}
                            onChange={handlePageSelectChange}
                            label="Navigate to ..."
                            style={{ width: '150px' }}
                        >
                            {Array.from({ length: state.totalPages }, (_, i) => i + 1).map(page => (
                                <MenuItem key={page} value={page}>
                                    Page {page}
                                </MenuItem>
                            ))}
                        </Select>
                    </Stack>
                </>
            )}
            <Dialog open={previewOffer != null} onClose={() => setPreviewOffer(null)} maxWidth={previewOffer?.htmlTemplateData ? 'xs' : 'sm'} fullWidth>
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Offer Preview
                    <IconButton onClick={() => setPreviewOffer(null)} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: previewOffer?.htmlTemplateData ? 0 : undefined }}>
                    {previewOffer && (
                        previewOffer.htmlTemplateData ? (
                            <Box sx={{width: '100%', aspectRatio: '9 / 16', position: 'relative'}}>
                                {htmlPreviewLoading ? (
                                    <Box sx={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <iframe
                                        title="HTML Offer Preview"
                                        srcDoc={htmlPreviewContent ?? ''}
                                        style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                                        sandbox="allow-scripts"
                                    />
                                )}
                            </Box>
                        ) : (
                            <Stack spacing={2} alignItems="center">
                                {previewOffer.iconUrl && (
                                    <img src={previewOffer.iconUrl} alt="offer"
                                         style={{ maxWidth: '100%', maxHeight: 240, objectFit: 'contain', borderRadius: 8 }} />
                                )}
                                <Typography variant="h6">{previewOffer.title || '—'}</Typography>
                                <Typography variant="body1" color="text.secondary">{previewOffer.subtitle || '—'}</Typography>
                                <Stack direction="row" spacing={4} justifyContent="center">
                                    <Box textAlign="center">
                                        <Typography variant="caption" color="text.secondary">Betcoins</Typography>
                                        <Typography variant="h6">{previewOffer.betcoins}</Typography>
                                    </Box>
                                    <Box textAlign="center">
                                        <Typography variant="caption" color="text.secondary">Product ID</Typography>
                                        <Typography variant="h6">{previewOffer.newPriceSku}</Typography>
                                    </Box>
                                </Stack>
                            </Stack>
                        )
                    )}
                </DialogContent>
            </Dialog>
            <Snackbar
                open={state.toastMsg != null}
                autoHideDuration={3000}
                message={state.toastMsg}
                onClose={() => setState(prevState => ({ ...prevState, toastMsg: null }))}
            />
        </>
    );
}
